import { useState, useEffect, useCallback, useMemo } from 'react';
import { api, type FeatureFlags } from '../shared/api-client';

interface FeatureFlagsState {
  flags: FeatureFlags;
  loading: boolean;
  error: string | null;
}

export function useFeatureFlags(): FeatureFlagsState & {
  isEnabled: (key: string) => boolean;
  refresh: () => Promise<void>;
} {
  const [state, setState] = useState<FeatureFlagsState>({
    flags: {},
    loading: true,
    error: null,
  });

  const loadFlags = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const result = await api.getFeatureFlags();

      if (result.success) {
        setState({ flags: result.data, loading: false, error: null });
      } else {
        setState({ flags: {}, loading: false, error: result.error.message });
      }
    } catch {
      setState({ flags: {}, loading: false, error: 'Failed to load feature flags' });
    }
  }, []);

  useEffect(() => {
    void loadFlags();
  }, [loadFlags]);

  const isEnabled = useCallback(
    (key: string): boolean => {
      return state.flags[key] ?? false;
    },
    [state.flags]
  );

  return useMemo(
    () => ({
      ...state,
      isEnabled,
      refresh: loadFlags,
    }),
    [state, isEnabled, loadFlags]
  );
}
